import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { EllipsizedValue } from 'customers/db-servers-detailed-view/EllipsizedValue';
import {
  COL_WIDTHS,
  stylingObject,
} from 'customers/db-servers-detailed-view/TessellAgentLcmInfoTab.styles';
import { toDisplayString } from 'customers/db-servers-detailed-view/TessellAgentLcmInfoTab.utils';

type AgentRow = {
  component: string;
  version?: unknown;
  last_version?: unknown;
  upgrade_status?: unknown;
  last_upgraded_at?: unknown;
};

type ColumnConfig = {
  id: string;
  label: string;
  width: number;
  getValue: (row: AgentRow) => unknown;
  renderCell?: (row: AgentRow) => JSX.Element;
};

const columns: ColumnConfig[] = [
  {
    id: 'component',
    label: 'Component',
    width: COL_WIDTHS.component,
    getValue: (row) => row.component,
    renderCell: (row) => (
      <Tooltip title={row.component} placement="right" arrow>
        <Typography
          variant="body2"
          sx={{
            maxWidth: COL_WIDTHS.component,
            ...stylingObject.monospaceEllipsis,
          }}
        >
          {row.component}
        </Typography>
      </Tooltip>
    ),
  },
  {
    id: 'version',
    label: 'Version',
    width: COL_WIDTHS.version,
    getValue: (row) => row.version,
  },
  {
    id: 'last_version',
    label: 'Last version',
    width: COL_WIDTHS.lastVersion,
    getValue: (row) => row.last_version,
  },
  {
    id: 'upgrade_status',
    label: 'Upgrade status',
    width: COL_WIDTHS.upgradeStatus,
    getValue: (row) => row.upgrade_status,
  },
  {
    id: 'last_upgraded_at',
    label: 'Last upgraded at',
    width: COL_WIDTHS.lastUpgradedAt,
    getValue: (row) => row.last_upgraded_at,
  },
];

export const TessellAgentLcmInfoTab = ({ dbServer }: { dbServer: any }) => {
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const data = dbServer?.tessellAgentLcmInfo?.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return [] as AgentRow[];
    }

    return Object.entries(data as Record<string, any>)
      .map(([component, info]) => ({
        component,
        version: info?.version,
        last_version: info?.last_version,
        upgrade_status: info?.upgrade_status,
        last_upgraded_at: info?.last_upgraded_at,
      }))
      .sort((a, b) => a.component.localeCompare(b.component));
  }, [dbServer]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const haystack = columns
        .map((column) => toDisplayString(column.getValue(r)))
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query, rows]);

  return (
    <Box sx={stylingObject.container}>
      <Stack spacing={1.5}>
        <Typography variant="h6" sx={stylingObject.title}>
          Agent LCM details
        </Typography>

        <TextField
          placeholder="Search keys or values…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="small"
          sx={stylingObject.search}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {filtered.length === 0 ? (
          <Box sx={stylingObject.emptyState}>
            <Typography variant="body2" color="text.secondary">
              {rows.length === 0
                ? 'No agent LCM data available for this DB server.'
                : 'No results match your search.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={stylingObject.tableContainer}>
            <Table size="small" sx={stylingObject.table}>
              <TableHead sx={stylingObject.tableHead}>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{ ...stylingObject.thCell, width: column.width }}
                    >
                      <Typography sx={stylingObject.th}>{column.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.component} hover>
                    {columns.map((column) => (
                      <TableCell key={column.id} sx={stylingObject.tdCell}>
                        {column.renderCell ? (
                          column.renderCell(r)
                        ) : (
                          <EllipsizedValue
                            value={column.getValue(r)}
                            valueTextSx={stylingObject.valueText}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Box>
  );
};

